import { collectApi } from "@/api/collect-api";
import { homeApi } from "@/api/home-api";
import { WSContext } from "@/router/router";
import { useContext, useEffect, useState } from "react";

export const useMarketData = () => {
  const [coinListData, setCoinListData] = useContext(WSContext);
  const [ctmarketlist, setCtmarketlist] = useState([] as any[]);
  const [collectlist, setcollectlist] = useState([] as any[]);

  // Format price with fallback
  const formatPrice = (price: number | undefined) => {
    if (typeof price !== 'number' || isNaN(price)) return '$0.00';
    return `$${price.toFixed(2)}`;
  };

  // Calculate change percentage with fallback
  const calculateChange = (close: number | undefined, open: number | undefined) => {
    if (typeof close !== 'number' || typeof open !== 'number' || isNaN(close) || isNaN(open) || open === 0) {
      return { text: '--', value: 0 };
    }
    const change = ((close - open) / open * 100);
    return {
      text: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
      value: change
    };
  };

  // Process market data to required format
  const processMarketData = () => {
    if (!coinListData || !ctmarketlist) return [];

    return ctmarketlist.map((item) => {
      if (!item?.coinname) return null;
      
      const priceData = coinListData[item.coinname] || {};
      const change = calculateChange(priceData.close, priceData.open);

      return {
        id: item.coinname,
        name: item.title?.split('/')[0] || item.coinname,
        price: formatPrice(priceData.close),
        change: change.text,
        logo: item.logo || '',
      };
    }).filter(Boolean);
  };

  // Process trending data (24h volume)
  const processTrendingData = () => {
    if (!coinListData || !ctmarketlist) return [];

    return ctmarketlist
      .map((item) => {
        if (!item?.coinname) return null;
        
        const priceData = coinListData[item.coinname] || {};
        const change = calculateChange(priceData.close, priceData.open);

        return {
          id: item.coinname,
          name: item.title?.split('/')[0] || item.coinname,
          price: formatPrice(priceData.close),
          change: change.text,
          logo: item.logo || '',
          volume: typeof priceData.vol === 'number' ? priceData.vol : 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 3)
      .map(({ volume, ...rest }) => rest);
  };

  // Process top gainers data
  const processTopGainersData = () => {
    if (!coinListData || !ctmarketlist) return [];

    return ctmarketlist
      .map((item) => {
        if (!item?.coinname) return null;
        
        const priceData = coinListData[item.coinname] || {};
        const change = calculateChange(priceData.close, priceData.open);

        return {
          id: item.coinname,
          name: item.title?.split('/')[0] || item.coinname,
          price: formatPrice(priceData.close),
          change: change.text,
          logo: item.logo || '',
          changeValue: change.value,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.changeValue - a.changeValue)
      .slice(0, 3)
      .map(({ changeValue, ...rest }) => rest);
  };

  const loadctmarketlistData = async () => {
    const data = await homeApi.ctmarketlist({ pageNum: 1, pageSize: 100 });
    if (data.ok) {
      const list = data.data.records;
      list.sort((d, e) => d.sort - e.sort);
      setCtmarketlist(list);
    }
  };

  function uniqueBySet(arr, key) {
    const uniqueSet = new Set();
    const uniqueArray = [];
    for (const item of arr) {
      const keyValue = item[key];
      if (!uniqueSet.has(keyValue)) {
        uniqueArray.push(item);
        uniqueSet.add(keyValue);
      }
    }
    return uniqueArray;
  }

  const uid = localStorage.getItem("uid");
  const loadcollectlistData = async () => {
    const data = await collectApi.list({ uid, pageNum: 1, pageSize: 100 });
    if (data.ok) {
      data.data = uniqueBySet(data.data, "coinname");
      setcollectlist(data.data);
    }
  };

  useEffect(() => {
    loadctmarketlistData();
    loadcollectlistData();
  }, []);

  const marketData = processMarketData();
  const trendingData = processTrendingData();
  const topGainersData = processTopGainersData();

  return {
    marketData,
    trendingData, 
    topGainersData,
    coinListData,
    ctmarketlist,
    collectlist,
  };
};
